import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import QuickStats from "../components/QuickStats";
import ApprovalCard from "../components/ApprovalCard";
import WorkOrdersTable from "../components/WorkOrdersTable";
import AccessDenied from "../../../shared/components/AccessDenied";
import Footer from "../../../shared/components/Footer";
import { useAuth } from "../../../shared/context/AuthContext";
import { useZone } from "../../../shared/context/ZoneContext";
import { getDashboardDataByZone } from "../data/dashboardMockData";
import { FileCheck, ChevronRight, Box as BoxIcon } from "@mynaui/icons-react";

const SupervisorDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedZone } = useZone();
  const { workOrders, requests } = getDashboardDataByZone(selectedZone);
  const [approvals, setApprovals] = useState(requests);

  useEffect(() => {
    setApprovals(requests);
  }, [selectedZone, requests]);

  // Access Control: Only supervisor can view this page
  if (user?.role !== "Supervisor") {
    return (
      <Box sx={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2, sm: 2.5, md: 3 },
            flexGrow: 1,
          }}
        >
          <AccessDenied />
        </Box>
        <Footer />
      </Box>
    );
  }

  const handleApprove = (workOrderId) => {
    console.log("Approved:", workOrderId);
    // Remove from approvals list
    setApprovals((prev) => prev.filter((item) => item.id !== workOrderId));
  };

  const handleReject = (workOrderId) => {
    console.log("Rejected:", workOrderId);
    // Remove from approvals list
    setApprovals((prev) => prev.filter((item) => item.id !== workOrderId));
  };

  return (
    <Box sx={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 2.5, md: 3, lg: 4 },
        }}
      >
        {/* Quick Stats Section */}
        <QuickStats
          awaitingApproval={approvals.length}
          workOrders={workOrders.length}
        />

        {/* Awaiting Approval and Work Orders Table */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
            gap: 2,
          }}
        >
          {/* Awaiting Approval container */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minWidth: 0,
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "24px",
              gap: "20px",
            }}
          >
            {/* Awaiting Approval header */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                flex: 1,
                minWidth: 0,
              }}
            >
              {/* left side */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <FileCheck size={24} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "600px",
                    fontSize: "18px",
                    lineHeight: "28px",
                  }}
                >
                  Awaiting Approval
                </Typography>
              </Box>

              {/* right side */}
              <Box
                onClick={() => navigate("/requests")}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  marginRight: "20px",
                  cursor: "pointer",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "20px",
                    color: "0F172B",
                  }}
                >
                  view all
                </Typography>
                <ChevronRight size={20} />
              </Box>
            </Box>

            {/* approval cards */}
            <Box>
              {approvals.slice(-2).map((request) => (
                <ApprovalCard
                  key={request.id}
                  workOrder={request}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onClick={(id) => navigate(`/requests/${id}`)}
                />
              ))}
            </Box>
          </Box>

          {/*Work Orders Table container */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1.3,
              minWidth: 0,
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "24px",
              gap: "20px",
            }}
          >
            {/* Work Orders Table header */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                minWidth: 0,
              }}
            >
              {/* left side */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <BoxIcon size={24} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "600px",
                    fontSize: "18px",
                    lineHeight: "28px",
                  }}
                >
                  Work Orders Assigned
                </Typography>
              </Box>

              {/* right side */}
              <Box
                onClick={() => navigate("/work-orders")}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                  marginRight: "20px",
                  cursor: "pointer",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "20px",
                    color: "#0F172B",
                  }}
                >
                  view all
                </Typography>
                <ChevronRight size={20} />
              </Box>
            </Box>

            {/* work orders table */}
            <Box>
              <WorkOrdersTable workOrders={workOrders.slice(-5)} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SupervisorDashboardPage;
